import { Formik } from "formik";
import Grid from "./layout/Grid";
import Panel from "./layout/Panel";
import Row from "./layout/Row";
import Col from "./layout/Col";
import Label from "./controls/Label";
import TextField from "./controls/TextField";
import {
  getTouchedFromSchema,
  isInvalid,
  touchFieldsFromErrors,
} from "../utils/forms";
import { DuctoSchema } from "../data/DuctoSchema";
import { useTranslation } from "react-i18next";
import CheckboxField from "./controls/CheckboxField";
import QuestionField from "./controls/QuestionField";
import ObjectField from "./controls/ObjectField";
import AmmountField from "./controls/AmmountField";
import PointField from "./controls/PointField";
import TextareaField from "./controls/TextareaField";
import ArrayField from "./controls/ArrayField";

import Pagination from "./controls/Pagination";
import Button from "./controls/Button";
import { CaudalMasicoForm } from "./CaudalMasicoForm";
import { FuncionamientoSchema } from "../data/FuncionamientoSchema";

export function DuctoForm({
  value,
  page,
  pages,
  select,
  append,
  replace,
  remove,
  operacionesUnitarias,
  unidadHoras,
  unidadDias,
  unidadMeses,
  unidadSeccion,
  unidadAltura,
  unidadDiametro,
  unidadTemperatura,
  unidadVelocidadSalida,
  unidadCaudal,
  contaminantes,
  disabled,
}) {
  const { t } = useTranslation();
  const initialTouched =
    page >= 0 ? getTouchedFromSchema(value, DuctoSchema) : {};
  const handleNumeroDuctoChange = (form) => (event) => {
    form.handleChange(event);
  };
  const handleReferenciaChange = (form) => (event) => {
    form.handleChange(event);
  };
  const handleOperacionesUnitariasChange = (form) => (event) => {
    form.handleChange(event);
  };
  const handleFuncionamientoContinuoChange = (form) => (event) => {
    if (event.target.checked) {
      form.setFieldValue("funcionamiento", {
        horas: { magnitud: 168, unidad: unidadHoras },
        dias: { magnitud: 7, unidad: unidadDias },
        meses: { magnitud: 12, unidad: unidadMeses },
      });
    } else {
      form.setFieldValue(
        "funcionamiento",
        FuncionamientoSchema.cast(undefined, { assert: false }),
      );
    }
    form.handleChange(event);
  };
  const handleFuncionamientoChange = (form) => (event) => {
    form.handleChange(event);
  };
  return (
    <Formik
      initialValues={value}
      enableReinitialize={true}
      validateOnChange={true}
      validateOnMount={page >= 0}
      initialTouched={initialTouched}
      validationSchema={DuctoSchema}
      onSubmit={(values) => {
        if (page === -1) {
          append(values);
        } else {
          replace(values, page);
        }
      }}
    >
      {(form) => (
        <Grid as={<Panel />} className="formulario embebido">
          <Row>
            <Col>
              <Label>{t("ductoForm.titulo")}</Label>
            </Col>
          </Row>
          <Row>
            <Col>
              <TextField
                id="ducto_numeroDucto"
                name="numeroDucto"
                label={t("ducto.numeroDucto")}
                value={form.values?.numeroDucto}
                onChange={handleNumeroDuctoChange(form)}
                isInvalid={isInvalid("numeroDucto", form)}
                error={t(form.errors.numeroDucto)}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <TextField
                id="ducto_referencia"
                name="referencia"
                label={t("ducto.referencia")}
                value={form.values?.referencia}
                onChange={handleReferenciaChange(form)}
                isInvalid={isInvalid("referencia", form)}
                error={t(form.errors.referencia)}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <CheckboxField
                id="ducto_operacionesUnitarias"
                name="operacionesUnitarias"
                label={t("ducto.operacionesUnitarias")}
                value={form.values?.operacionesUnitarias}
                onChange={handleOperacionesUnitariasChange(form)}
                options={operacionesUnitarias}
                labelAttr="nombre"
                valueAttr="orden"
                lg={6}
                isInvalid={isInvalid("operacionesUnitarias", form)}
                error={t(form.errors.operacionesUnitarias)}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <QuestionField
                id="ducto_funcionamientoContinuo"
                name="funcionamientoContinuo"
                label={t("ducto.funcionamientoContinuo")}
                value={form.values?.funcionamientoContinuo}
                onChange={handleFuncionamientoContinuoChange(form)}
                isInvalid={isInvalid("funcionamientoContinuo", form)}
                error={t(form.errors.funcionamientoContinuo)}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <ObjectField
                id="ducto_funcionamiento"
                name="funcionamiento"
                value={form.values?.funcionamiento}
                onChange={handleFuncionamientoChange(form)}
                disabled={disabled || form.values.funcionamientoContinuo}
              >
                {({ value, setProperty, disabled }) => (
                  <Grid>
                    <Row>
                      <Col lg={4}>
                        <AmmountField
                          id="ducto_funcionamiento_horas"
                          name="funcionamiento.horas"
                          label={t("funcionamiento.horas")}
                          value={value?.horas}
                          onChange={(event) => {
                            event.preventDefault();
                            setProperty("horas", event.target.value);
                          }}
                          unit={unidadHoras}
                          isInvalid={isInvalid("funcionamiento.horas", form)}
                          error={t(form.errors.funcionamiento?.horas)}
                          disabled={disabled}
                        />
                      </Col>
                      <Col lg={4}>
                        <AmmountField
                          id="ducto_funcionamiento_dias"
                          name="funcionamiento.dias"
                          label={t("funcionamiento.dias")}
                          value={value?.dias}
                          onChange={(event) => {
                            event.preventDefault();
                            setProperty("dias", event.target.value);
                          }}
                          unit={unidadDias}
                          isInvalid={isInvalid("funcionamiento.dias", form)}
                          error={t(form.errors.funcionamiento?.dias)}
                          disabled={disabled}
                        />
                      </Col>
                      <Col lg={4}>
                        <AmmountField
                          id="ducto_funcionamiento_meses"
                          name="funcionamiento.meses"
                          label={t("funcionamiento.meses")}
                          value={value?.meses}
                          onChange={(event) => {
                            event.preventDefault();
                            setProperty("meses", event.target.value);
                          }}
                          unit={unidadMeses}
                          isInvalid={isInvalid("funcionamiento.meses", form)}
                          error={t(form.errors.funcionamiento?.meses)}
                          disabled={disabled}
                        />
                      </Col>
                    </Row>
                  </Grid>
                )}
              </ObjectField>
            </Col>
          </Row>
          <Row>
            <Col>
              <PointField
                id="ducto_ubicacion"
                name="ubicacion"
                label={t("ducto.ubicacion")}
                value={form.values?.ubicacion}
                onChange={(event) => form.handleChange(event)}
                isInvalid={isInvalid("ubicacion", form)}
                error={t(form.errors.ubicacion)}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col lg={4}>
              <AmmountField
                id="ducto_seccion"
                name="seccion"
                label={t("ducto.seccion")}
                value={form.values?.seccion}
                onChange={(event) => form.handleChange(event)}
                unit={unidadSeccion}
                isInvalid={isInvalid("seccion", form)}
                error={t(form.errors.seccion)}
                disabled={disabled}
              />
            </Col>
            <Col lg={4}>
              <AmmountField
                id="ducto_altura"
                name="altura"
                label={t("ducto.altura")}
                value={form.values?.altura}
                onChange={(event) => form.handleChange(event)}
                unit={unidadAltura}
                isInvalid={isInvalid("altura", form)}
                error={t(form.errors.altura)}
                disabled={disabled}
              />
            </Col>
            <Col lg={4}>
              <AmmountField
                id="ducto_diametro"
                name="diametro"
                label={t("ducto.diametro")}
                value={form.values?.diametro}
                onChange={(event) => form.handleChange(event)}
                unit={unidadDiametro}
                isInvalid={isInvalid("diametro", form)}
                error={t(form.errors.diametro)}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col lg={4}>
              <AmmountField
                id="ducto_temperatura"
                name="temperatura"
                label={t("ducto.temperatura")}
                value={form.values?.temperatura}
                onChange={(event) => form.handleChange(event)}
                unit={unidadTemperatura}
                isInvalid={isInvalid("temperatura", form)}
                error={t(form.errors.temperatura)}
                disabled={disabled}
              />
            </Col>
            <Col lg={4}>
              <AmmountField
                id="ducto_velocidadSalida"
                name="velocidadSalida"
                label={t("ducto.velocidadSalida")}
                value={form.values?.velocidadSalida}
                onChange={(event) => form.handleChange(event)}
                unit={unidadVelocidadSalida}
                isInvalid={isInvalid("velocidadSalida", form)}
                error={t(form.errors.velocidadSalida)}
                disabled={disabled}
              />
            </Col>
            <Col lg={4}>
              <AmmountField
                id="ducto_caudal"
                name="caudal"
                label={t("ducto.caudal")}
                value={form.values?.caudal}
                onChange={(event) => form.handleChange(event)}
                unit={unidadCaudal}
                isInvalid={isInvalid("caudal", form)}
                error={t(form.errors.caudal)}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <TextareaField
                id="ducto_descripcionTratamiento"
                name="descripcionTratamiento"
                label={t("ducto.descripcionTratamiento")}
                value={form.values?.descripcionTratamiento}
                onChange={(event) => form.handleChange(event)}
                rows={12}
                isInvalid={isInvalid("descripcionTratamiento", form)}
                error={t(form.errors.descripcionTratamiento)}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Label>{t("ducto.caudalMasico")}</Label>
              <ArrayField
                id="ducto_caudalMasico"
                name="caudalMasico"
                value={form.values?.caudalMasico}
                onChange={(event) => form.handleChange(event)}
                isInvalid={isInvalid("caudalMasico", form)}
                error={t(form.errors.caudalMasico)}
                disabled={disabled}
              >
                {({
                  value: mediciones,
                  selectedItem,
                  append,
                  remove,
                  disabled,
                }) => (
                  <CaudalMasicoForm
                    value={mediciones}
                    selectedItem={selectedItem}
                    append={append}
                    remove={remove}
                    contaminantes={contaminantes}
                    disabled={disabled}
                  />
                )}
              </ArrayField>
            </Col>
          </Row>
          <Row style={{ display: "flex", flexWrap: "wrap" }}>
            <Col lg={7}>
              <Pagination
                sm
                page={page}
                pages={pages}
                onChange={(event) => select(event.target.value)}
              />
            </Col>
            <Col lg={5}>
              <span
                style={{
                  float: "right",
                  alignContent: "center",
                }}
              >
                {page !== -1 && (
                  <Button
                    sm
                    variant="success"
                    value={t("ductoForm.nuevo")}
                    onClick={(event) => {
                      event.preventDefault();
                      select(-1);
                    }}
                    disabled={disabled}
                  />
                )}
                {page !== -1 && (
                  <Button
                    sm
                    variant="danger"
                    value={t("ductoForm.eliminar")}
                    onClick={(event) => {
                      event.preventDefault();
                      remove(page);
                    }}
                    disabled={disabled}
                  />
                )}
                <Button
                  sm
                  variant="primary"
                  value={t("ductoForm.guardar")}
                  onClick={(event) => {
                    event.preventDefault();
                    form.validateForm().then((errors) => {
                      if (touchFieldsFromErrors(errors, form)) {
                        return;
                      }
                      form.submitForm();
                    });
                  }}
                  disabled={disabled}
                />
                {page === -1 && pages > 0 && (
                  <Button
                    sm
                    variant="default"
                    value={t("ductoForm.cancelar")}
                    onClick={(event) => {
                      event.preventDefault();
                      select(0);
                    }}
                    disabled={disabled}
                  />
                )}
              </span>
            </Col>
          </Row>
        </Grid>
      )}
    </Formik>
  );
}
